import { memo, useCallback, useContext } from 'react';
import type * as THREE from 'three';
import { createBox3, createGroup, createObject3D, createVector3 } from '../../../../utils/potreeThree';
import Focus from '../../../../assets/icons/focus.svg?react';
import { Annotation as TypeAnnotation, ProjectFragment, OrthophotoLayer } from '../../../../types/graphqlTypes';
import { RendererContext, RendererContextType } from '../../../../contexts/RendererContext';
import classNames from 'classnames';
import { HideSelect, HideSelectorProps } from '../../../../components/HideSelector';
import { useToggleHide } from '../../../../hooks/modules/project/useToggleHide';
import { AnnotationContextGroup, collectLeafAnnotations } from '../../../../contexts/AnnotationContext';
import { CadLayer } from '../../../../contexts/CadLayersContext';
import { CadObject, ICadObjectGroup } from '../../../../utils/CADFileParser';
import { GroupFilterIcon } from './GroupFilterToggle';
import { AnnotationsFilterIcon } from './FilterToggle';
import { FloorIsolationToggle } from './FloorIsolationToggle';
import { GroupType } from '../../../../types/graphqlTypes';
import { getAnnotationVisibleProperty } from '@pointorama/potree-shared';
import { useOpeningsByRoomId } from '../../../../hooks/potree/useOpeningsByRoomId';
import { OpeningAnnotation } from '../../../../potree/utils/PointoramaAnnotation';
import { computeOpeningFrame } from '../../../../potree/personas/tools/modules/openingGizmos/OpeningFrame';

/** Expands a bounding box to encompass the scene representation of an annotation. */
const expandBoundsWithAnnotation = (boundingBox: THREE.Box3, annotation: { identifier: string; type?: string }, viewer: NonNullable<RendererContextType['viewer']>) => {
  const { scene } = viewer;
  const { identifier, type } = annotation;

  switch (type) {
    case 'BOX': {
      const volume = scene.volumes.find((v) => v.identifier === identifier);
      if (volume) {
        boundingBox.expandByPoint(volume.position.clone().add(volume.scale.clone().divideScalar(2.0)));
        boundingBox.expandByPoint(volume.position.clone().sub(volume.scale.clone().divideScalar(2.0)));
      }
      break;
    }
    case 'POINTCLUSTER': {
      const cluster = scene.pointClusters.find((c) => c.identifier === identifier);
      if (cluster) {
        for (const segment of cluster.segments) {
          const segBounds = (scene.pointclouds.find((pc) => pc.identifier === segment.pointcloudId)?.segmentList[segment.segmentId] as { boundingBox?: { p1: THREE.Vector3; p2: THREE.Vector3 } } | undefined)?.boundingBox;
          if (segBounds) {
            boundingBox.expandByPoint(segBounds.p1);
            boundingBox.expandByPoint(segBounds.p2);
          }
        }
      }
      break;
    }
    case 'ROOM': {
      const room = scene.rooms.find((r) => r.identifier === identifier);
      if (room?.boundingSphere) {
        // boundingSphere is in local space (relative to room.position)
        const { center, radius } = room.boundingSphere;
        const cx = room.position.x + center.x;
        const cy = room.position.y + center.y;
        const cz = room.position.z + center.z;
        boundingBox.expandByPoint(createVector3(cx - radius, cy - radius, cz - radius));
        boundingBox.expandByPoint(createVector3(cx + radius, cy + radius, cz + radius));
      }
      break;
    }
    case 'STRUCTURE': {
      const structure = scene.structures.find((s) => s.identifier === identifier);
      if (structure?.boundingSphere) {
        // boundingSphere is in local space (relative to structure.position), like ROOM
        const { center, radius } = structure.boundingSphere;
        const cx = structure.position.x + center.x;
        const cy = structure.position.y + center.y;
        const cz = structure.position.z + center.z;
        boundingBox.expandByPoint(createVector3(cx - radius, cy - radius, cz - radius));
        boundingBox.expandByPoint(createVector3(cx + radius, cy + radius, cz + radius));
      }
      break;
    }
    case 'OPENING_SKETCH': {
      // Opening sketches are parametric (a segment on a wall-sketch edge), not
      // stored in scene.measurements. The scene object derives its own world box.
      const openingSketch = scene.openingSketches.find((o) => o.identifier === identifier);
      const box = openingSketch?.getBoundingBox();
      if (box) boundingBox.union(box);
      break;
    }
    case 'SKETCH_OBJECT': {
      // Sketch objects are parametric (anchor / wall attachment in reactive
      // properties); the scene object derives its own world box from its frame.
      const sketchObject = scene.sketchObjects.find((o) => o.identifier === identifier);
      const box = sketchObject?.getBoundingBox();
      if (box) boundingBox.union(box);
      break;
    }
    // POINT, DISTANCE, AREA, HEIGHT, LINE are default Potree measurements.
    // These share a common points array to use for bounding box.
    default: {
      const measurement = scene.measurements.find((m) => m.identifier === identifier);
      if (measurement) {
        measurement.points.forEach((point) => {
          boundingBox.expandByPoint(point.position);
        });
      }
      break;
    }
  }
};

interface AnnotationsIconsProps {
  identifier: string;
  visible?: boolean;
  disabled?: boolean;
  project?: Pick<ProjectFragment, 'id'>;
  annotation?: TypeAnnotation;
  pointCloud?: boolean;
  group?: AnnotationContextGroup;
  onToggleHide?: (event: React.MouseEvent) => void;
  cadObject?: CadObject;
  cadObjectGroup?: ICadObjectGroup;
  cadLayer?: CadLayer;
  orthophoto?: OrthophotoLayer;
}

const AnnotationsEyeIcon_: React.FC2<AnnotationsIconsProps> = ({ identifier, visible, group, ...props }) => {
  const { toggleHide } = useToggleHide();
  const { byRoomId } = useOpeningsByRoomId();

  const onToggleHide: Required<HideSelectorProps>['onClick'] = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (props.onToggleHide) return props.onToggleHide(event);
      let identifiers;
      if (group) {
        // Leaves only: descend into nested room folders (their sketches must
        // toggle with the floor). A folder node's type is the GroupType string
        // 'ROOM' — never confuse it with a ROOM annotation.
        identifiers = collectLeafAnnotations(group).flatMap((annotation) => (annotation.type === 'ROOM' ? [annotation.identifier, ...(byRoomId[annotation.identifier] ?? [])] : [annotation.identifier]));
      } else {
        identifiers = [identifier, ...(byRoomId[identifier] ?? [])];
      }
      toggleHide({ identifiers, visible: !visible });
    },
    [props, group, toggleHide, visible, identifier, byRoomId],
  );

  return <HideSelect onClick={onToggleHide} hidden={!visible} />;
};
export const AnnotationsEyeIcon = memo(AnnotationsEyeIcon_);

const AnnotationsFocusIcon_: React.FC2<AnnotationsIconsProps> = ({ identifier, visible, disabled, annotation, pointCloud, group, cadObject, cadLayer, orthophoto, cadObjectGroup }) => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  const onFocusAnnotation = useCallback(() => {
    // We choose to select the annotation when using the focus tool.
    if (!viewer) return;

    if (annotation) {
      const boundingBox = createBox3();
      const node: { boundingBox?: THREE.Box3 } & THREE.Object3D = createObject3D();
      node.boundingBox = boundingBox;
      expandBoundsWithAnnotation(boundingBox, annotation, viewer);
      if (boundingBox.isEmpty()) return;
      // Expand is done to correct single point zoom behavior.
      if (annotation.type === 'POINT') {
        boundingBox.expandByVector(createVector3(1, 1, 0.0));
      }
      // An opening is a flat vertical rectangle on a wall. zoomTo preserves the
      // current view direction, so from a top-down view the opening is seen
      // edge-on (a thin line). Compute a look direction along the opening's
      // normal so zoomTo frames it head-on, matching how walls are focused.
      let lookDirection: THREE.Vector3 | undefined;
      if (annotation.type === 'OPENING') {
        const opening = viewer.scene.measurements.find((m) => m.identifier === annotation.identifier);
        const frame = opening instanceof OpeningAnnotation ? computeOpeningFrame(opening) : null;
        if (frame) {
          // Face the wall from the front; the normal sign is arbitrary, so pick
          // the side the camera is currently on to avoid flipping through the
          // wall. The look direction points from that side into the opening.
          const toCamera = viewer.scene.view.position.clone().sub(frame.center);
          const facing = frame.normal.dot(toCamera) < 0 ? frame.normal.clone().negate() : frame.normal.clone();
          lookDirection = facing.negate();
        }
      }
      viewer.zoomTo(node, 1, 500, lookDirection);
    } else if (group?.type === GroupType.Surface) {
      // Surface groups hold no child annotations; frame the surface scene object.
      const surface = viewer.scene.surfaces?.find((s) => s.identifier === group.identifier);
      if (!surface?.boundingSphere) return;
      const boundingBox = createBox3();
      const node: { boundingBox?: THREE.Box3 } & THREE.Group = createGroup();
      node.boundingBox = boundingBox;
      const r = surface.boundingSphere.radius;
      const c = surface.position;
      boundingBox.expandByPoint(createVector3(c.x - r, c.y - r, c.z - r));
      boundingBox.expandByPoint(createVector3(c.x + r, c.y + r, c.z + r));
      viewer.zoomTo(node, 5, 500);
    } else if (group) {
      const boundingBox = createBox3();
      const node: { boundingBox?: THREE.Box3 } & THREE.Group = createGroup();
      node.boundingBox = boundingBox;

      // Leaves only: sketches filed in nested room folders count toward the
      // floor's focus bounds; folder nodes have no scene geometry.
      const leafAnnotations = collectLeafAnnotations(group);
      leafAnnotations.forEach((annotation) => {
        try {
          const visible = getAnnotationVisibleProperty(annotation.identifier).value;
          if (visible !== false) {
            expandBoundsWithAnnotation(boundingBox, annotation, viewer);
          }
        } catch {
          expandBoundsWithAnnotation(boundingBox, annotation, viewer);
        }
      });
      if (boundingBox.isEmpty()) return;
      if (leafAnnotations.length === 1 && leafAnnotations[0].type === 'POINT') {
        // Expand is done to correct single point zoom behavior.
        boundingBox.expandByVector(createVector3(1, 1, 0.0));
      }
      viewer.zoomTo(node, 1, 500);
    } else if (cadObject || cadObjectGroup || cadLayer) {
      const elements = cadLayer?.cadObjectGroups?.flatMap((cadObjectGroup) => cadObjectGroup.cadObjects.flatMap((cadObject) => cadObject)) || cadObjectGroup?.cadObjects.flatMap((cadObject) => cadObject) || (cadObject ? [cadObject] : []);
      const boundingBox = createBox3();
      const node: { boundingBox?: THREE.Box3 } & THREE.Group = createGroup();
      node.boundingBox = boundingBox;

      elements.forEach((cadObject) => {
        if (cadObject.visible) {
          const cadMeasure = viewer.scene.measurements.find((measure) => measure.identifier === cadObject.identifier);
          if (cadMeasure) {
            cadMeasure.points.forEach((point) => {
              boundingBox.expandByPoint(point.position);
            });
          }
        }
      });
      if (boundingBox.isEmpty()) return;
      boundingBox.expandByVector(createVector3(10.0, 10.0, 0.0));
      viewer.zoomTo(node, 1, 500);
    } else if (pointCloud) {
      const node = viewer.scene.pointclouds.find((node) => node.identifier === identifier);
      if (!node) return;
      viewer.zoomTo(node, 1, 500);
    } else if (orthophoto) {
      const node = viewer.scene.orthophotos.find((node) => node.identifier === identifier)?.mesh;
      if (!node) return;
      viewer.zoomTo(node, 1, 500);
    } else {
      console.warn('AnnotationsFocusIcon: unhandled focus target', { identifier });
    }
  }, [viewer, annotation, cadObject, cadObjectGroup, cadLayer, pointCloud, orthophoto, group, identifier]);

  return (
    <div
      onClick={onFocusAnnotation}
      className={classNames(
        'flex items-center justify-center w-6 h-6 stroke-current rounded-sm',
        disabled || !visible ? 'cursor-not-allowed text-gray-400 dark:text-[#6f717357]' : 'cursor-pointer text-gray-700 hover:text-black dark:hover:text-neon-green-300',
      )}
    >
      <Focus className="w-4 h-4" />
    </div>
  );
};
export const AnnotationsFocusIcon = memo(AnnotationsFocusIcon_);

const AnnotationsIconsWrapper_: React.FC2<AnnotationsIconsProps> = ({ ...props }) => {
  // A SURFACE group is itself a filterable clip volume (its filter/clip_method
  // properties are keyed by the group identifier), so it gets the per-identifier
  // filter toggle rather than the child-aggregating GroupFilterIcon.
  const surfaceGroup = props.group?.type === GroupType.Surface ? props.group : undefined;
  return (
    <div className="flex justify-end grow">
      {props.group?.type === GroupType.Floor && <FloorIsolationToggle project={props.project} group={props.group} />}
      {surfaceGroup ? (
        <AnnotationsFilterIcon project={props.project} annotation={{ identifier: surfaceGroup.identifier, type: 'SURFACE' as TypeAnnotation['type'] }} />
      ) : (
        props.group && <GroupFilterIcon project={props.project} group={props.group} />
      )}
      <AnnotationsFocusIcon {...props} />
      <AnnotationsEyeIcon {...props} />
    </div>
  );
};
export const AnnotationsIconsWrapper = memo(AnnotationsIconsWrapper_);
